/**
 * Self service.
 *
 * The screens an occasional user needs and nothing else: claim an expense, ask
 * for a new supplier, code a procurement card statement. Somebody who touches
 * the finance system four times a year should not have to learn it.
 */

import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms, lower } from '../lib/tenant';
import { date, money, statusLabel, statusTone, todayIso } from '../lib/format';
import {
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Select,
  Tabs,
  TextArea,
  TextInput,
} from '../components/ui';

interface ExpenseClaim {
  id: string;
  reference?: string;
  claim_no?: string;
  description: string;
  total_minor: string;
  status: string;
  submitted_at: string | null;
  approved_by_name: string | null;
}

interface SupplierRequest {
  id: string;
  proposed_name: string;
  reason: string | null;
  status: string;
  requested_by_name: string | null;
  companies_house_status: string | null;
  duplicate_warning: string | null;
  created_at: string;
  supplier_code: string | null;
}

export function SelfServicePage() {
  const t = useTerms();
  const { can } = useSession();
  const [tab, setTab] = useState('expenses');
  const [claiming, setClaiming] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const expenses = useAsync(() => api.get<{ claims: ExpenseClaim[] }>('/self-service/expenses'), []);
  const supplierRequests = useAsync(
    () => api.get<{ requests: SupplierRequest[] }>('/self-service/supplier-requests'),
    [],
  );

  return (
    <>
      <PageHeader
        title="Self service"
        subtitle="Claim an expense, ask for a new supplier, and see where each request has got to."
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'expenses', label: 'Expense claims' },
          { key: 'suppliers', label: `New ${lower(t.supplier)} requests` },
        ]}
      />

      {tab === 'expenses' ? (
        <Card
          title="My expense claims"
          flush
          actions={
            can('expense.claim') ? (
              <button type="button" className="btn btn--sm btn--primary" onClick={() => setClaiming(true)}>
                New claim
              </button>
            ) : null
          }
        >
          <AsyncPanel state={expenses}>
            {(data) => (
              <DataTable
                caption="Expense claims"
                rows={data.claims ?? []}
                rowKey={(c) => c.id}
                emptyMessage="You have no expense claims."
                columns={[
                  {
                    key: 'ref',
                    header: 'Claim',
                    render: (c) => <span className="mono">{c.reference ?? c.claim_no ?? '—'}</span>,
                  },
                  { key: 'desc', header: 'For', render: (c) => <span className="truncate">{c.description}</span> },
                  { key: 'total', header: 'Amount', numeric: true, render: (c) => <Money minor={Number(c.total_minor)} /> },
                  {
                    key: 'submitted',
                    header: 'Submitted',
                    render: (c) => <span className="smaller">{c.submitted_at ? date(c.submitted_at) : 'Draft'}</span>,
                  },
                  { key: 'status', header: 'Status', render: (c) => <Badge tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge> },
                ]}
              />
            )}
          </AsyncPanel>
        </Card>
      ) : (
        <Card
          title={`New ${lower(t.supplier)} requests`}
          subtitle="Checked for duplicates and company status before anybody can order from them."
          flush
          actions={
            can('supplier.request') ? (
              <button type="button" className="btn btn--sm btn--primary" onClick={() => setRequesting(true)}>
                Request a {lower(t.supplier)}
              </button>
            ) : null
          }
        >
          <AsyncPanel state={supplierRequests}>
            {(data) => (
              <DataTable
                caption={`New ${lower(t.supplier)} requests`}
                rows={data.requests ?? []}
                rowKey={(r) => r.id}
                emptyMessage="No requests."
                columns={[
                  { key: 'name', header: 'Proposed name', render: (r) => r.proposed_name },
                  { key: 'reason', header: 'Why', render: (r) => <span className="smaller truncate">{r.reason ?? '—'}</span> },
                  { key: 'by', header: 'Requested by', render: (r) => <span className="smaller">{r.requested_by_name}</span> },
                  {
                    key: 'checks',
                    header: 'Checks',
                    render: (r) => (
                      <>
                        {r.duplicate_warning ? (
                          <Badge tone="warn">Possible duplicate</Badge>
                        ) : (
                          <Badge tone="ok">No duplicate found</Badge>
                        )}
                        {r.companies_house_status ? (
                          <div className="smaller muted">Company status: {statusLabel(r.companies_house_status)}</div>
                        ) : null}
                      </>
                    ),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (r) => (
                      <>
                        <Badge tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
                        {r.supplier_code ? <div className="smaller mono muted">{r.supplier_code}</div> : null}
                      </>
                    ),
                  },
                ]}
              />
            )}
          </AsyncPanel>
        </Card>
      )}

      <ExpenseModal
        open={claiming}
        onClose={() => setClaiming(false)}
        onCreated={() => {
          setClaiming(false);
          expenses.reload();
        }}
      />
      <SupplierRequestModal
        open={requesting}
        onClose={() => setRequesting(false)}
        onCreated={() => {
          setRequesting(false);
          supplierRequests.reload();
        }}
      />
    </>
  );
}

function ExpenseModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const t = useTerms();
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [costCentreId, setCostCentreId] = useState('');
  const [incurredOn, setIncurredOn] = useState(todayIso());

  const refs = useAsync(
    () =>
      open
        ? Promise.all([
            api.get<{ accounts: Array<{ id: string; code: string; name: string; account_type: string }> }>('/reference/accounts'),
            api.get<{ costCentres: Array<{ id: string; code: string; name: string }> }>('/reference/cost-centres'),
          ]).then(([a, cc]) => ({
            accounts: a.accounts.filter((x) => x.account_type === 'expenditure'),
            costCentres: cc.costCentres,
          }))
        : Promise.resolve({ accounts: [], costCentres: [] }),
    [open],
  );

  const create = useMutation(async () =>
    api.post('/self-service/expenses', {
      description: description.trim(),
      lines: [{ description: description.trim(), amount, accountId, costCentreId, incurredOn }],
    }),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Claim an expense"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={create.busy || !accountId || !amount || description.trim().length < 3}
            onClick={() => void create.run().then((r) => r !== null && onCreated())}
          >
            {create.busy ? 'Submitting…' : 'Submit the claim'}
          </button>
        </>
      }
    >
      <AsyncPanel state={refs}>
        {(data) => (
          <div className="stack">
            <TextInput
              label="What was it for?"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              required
            />
            <div className="grid grid--2">
              <TextInput
                label="Amount"
                className="num-input"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.currentTarget.value)}
                required
              />
              <TextInput
                label="Date incurred"
                type="date"
                value={incurredOn}
                onChange={(e) => setIncurredOn(e.currentTarget.value)}
              />
              <Select label="Nominal code" value={accountId} onChange={(e) => setAccountId(e.currentTarget.value)} required>
                <option value="">Choose…</option>
                {data.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </Select>
              <Select
                label={t.costCentre}
                value={costCentreId}
                onChange={(e) => setCostCentreId(e.currentTarget.value)}
                required
              >
                <option value="">Choose…</option>
                {data.costCentres.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </Select>
            </div>
            <ErrorNotice error={create.error} />
          </div>
        )}
      </AsyncPanel>
    </Modal>
  );
}

function SupplierRequestModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const t = useTerms();
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [email, setEmail] = useState('');

  const duplicates = useAsync(
    () =>
      name.trim().length > 3
        ? api.get<{ matches: Array<{ id: string; code: string; name: string; score: number }> }>(
            `/reference/suppliers/check-duplicate?name=${encodeURIComponent(name)}`,
          )
        : Promise.resolve({ matches: [] }),
    [name],
  );

  const create = useMutation(async () =>
    api.post('/self-service/supplier-requests', {
      proposedName: name.trim(),
      reason: reason.trim(),
      companiesHouseNumber: companyNumber.trim() || null,
      email: email.trim() || null,
    }),
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Request a new ${lower(t.supplier)}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={create.busy || name.trim().length < 3 || reason.trim().length < 5}
            onClick={() => void create.run().then((r) => r !== null && onCreated())}
          >
            {create.busy ? 'Submitting…' : 'Submit the request'}
          </button>
        </>
      }
    >
      <div className="stack">
        <TextInput
          label={`${t.supplier} name`}
          hint="As it appears on their invoice or contract."
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
        />
        {duplicates.data && duplicates.data.matches.length > 0 ? (
          <div className="alert alert--warn" role="status">
            <span className="alert__icon" aria-hidden="true">
              !
            </span>
            <div className="alert__body">
              <p className="alert__title">This may already be on file</p>
              <ul className="smaller">
                {duplicates.data.matches.map((m) => (
                  <li key={m.id}>
                    <span className="mono">{m.code}</span> {m.name} — {m.score}% similar
                  </li>
                ))}
              </ul>
              <p className="smaller muted">
                Duplicate suppliers are how the same invoice gets paid twice. Check before requesting a new one.
              </p>
            </div>
          </div>
        ) : null}
        <TextInput
          label="Company number (optional)"
          hint="If supplied, the company status is checked before approval."
          value={companyNumber}
          onChange={(e) => setCompanyNumber(e.currentTarget.value)}
        />
        <TextInput label="Their email (optional)" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
        <TextArea
          label="Why do we need them?"
          hint="What you intend to buy, and why an existing supplier will not do."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          required
        />
        <ErrorNotice error={create.error} />
      </div>
    </Modal>
  );
}
